import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPinIcon, BriefcaseIcon, ArrowRightIcon } from 'lucide-react';
import { PortraitFrame } from '../components/PortraitFrame';
import { SectionDirectory } from '../components/SectionDirectory';
import { profile } from '../data/profile';
const fade = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.28,
      delay: index * 0.05,
      ease: [0.23, 1, 0.32, 1] as const
    }
  })
};
export function BoardPositioning() {
  return <div className="bg-white" data-id="element-192" data-mp="pw1w8nxjrk9t">
      <section className="relative overflow-hidden bg-navy-900 pb-20 pt-28 lg:pb-28 lg:pt-36" aria-labelledby="hero-heading" data-id="element-193" data-mp="pv08bivfn3ao">
        <div aria-hidden="true" className="absolute inset-y-0 right-0 hidden w-[38%] border-l border-white/5 bg-navy-800 lg:block" data-id="element-194" data-mp="uwzdmyaqgaa9" />

        <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-12 lg:gap-14 lg:px-10" data-id="element-195" data-mp="3fm569ier6lj4">
          <div className="lg:col-span-7" data-id="element-196" data-mp="qu2g35y0ccav">
            <motion.p custom={0} initial="hidden" animate="visible" variants={fade} className="font-sans text-xs font-semibold uppercase tracking-[0.24em] text-gold-400" data-id="element-197" data-mp="ltxzzh10hjl6">
              Section 04 · Executive Hero &amp; Board Positioning
            </motion.p>

            <motion.h1 id="hero-heading" custom={1} initial="hidden" animate="visible" variants={fade} className="mt-5 font-display text-5xl font-extrabold leading-[1.02] tracking-tightest text-white sm:text-6xl xl:text-7xl" data-id="element-198" data-mp="19p0o1mrzrj0l">
              {profile.name}
            </motion.h1>

            <motion.p custom={2} initial="hidden" animate="visible" variants={fade} className="mt-5 max-w-2xl font-display text-lg font-semibold leading-snug text-platinum-200 sm:text-xl" data-id="element-199" data-mp="1si4k1esom7yd">
              {profile.title}
            </motion.p>

            <motion.div custom={3} initial="hidden" animate="visible" variants={fade} className="mt-7 max-w-2xl border-l-2 border-gold-500 pl-5" data-id="element-200" data-mp="20g6jxu53nki2">
              <p className="font-sans text-base leading-relaxed text-platinum-300 sm:text-lg" data-id="element-201" data-mp="2e9xpysjmgm91">
                {profile.valueStatement}
              </p>
            </motion.div>

            <motion.dl custom={4} initial="hidden" animate="visible" variants={fade} className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-3" data-id="element-202" data-mp="2jalf18oait0m">
              <div className="flex items-center gap-2" data-id="element-203" data-mp="tum2ripdkngr">
                <MapPinIcon className="h-4 w-4 shrink-0 text-gold-400" aria-hidden="true" data-id="element-204" data-mp="tvx318jm3d34" />
                <dt className="sr-only" data-id="element-205" data-mp="yv1ds3m6pivk">Location</dt>
                <dd className="font-sans text-sm text-platinum-200" data-id="element-206" data-mp="3c3glio86dm4p">{profile.location}</dd>
              </div>
              <div className="flex items-center gap-2" data-id="element-207" data-mp="37bfcdb6b0biw">
                <BriefcaseIcon className="h-4 w-4 shrink-0 text-gold-400" aria-hidden="true" data-id="element-208" data-mp="33awlbjc8j9hr" />
                <dt className="sr-only" data-id="element-209" data-mp="2ybsbqrlef9qw">Seniority</dt>
                <dd className="font-sans text-sm text-platinum-200" data-id="element-210" data-mp="2yd3d6krdg5eu">{profile.seniority}</dd>
              </div>
            </motion.dl>

            <motion.div custom={5} initial="hidden" animate="visible" variants={fade} className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-id="element-211" data-mp="3uzq9q4csjt2f">
              <Link to="/about" className="group inline-flex items-center justify-center gap-2 whitespace-nowrap bg-gold-500 px-6 py-3.5 font-sans text-sm font-semibold text-navy-950 transition-colors duration-150 ease-out hover:bg-gold-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold-400" data-id="element-212" data-mp="3h2t95b6vex8c">
                Executive Profile
                <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-smooth group-hover:translate-x-1" aria-hidden="true" data-id="element-213" data-mp="26pwavizt50v" />
              </Link>
              <Link to="/board-education" className="inline-flex items-center justify-center whitespace-nowrap border border-white/25 px-6 py-3.5 font-sans text-sm font-semibold text-white transition-colors duration-150 ease-out hover:border-white/60 hover:bg-white/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold-400" data-id="element-214" data-mp="3pih7spbihesk">
                Board &amp; Governance Credentials
              </Link>
              <Link to="/contact" className="inline-flex items-center justify-center whitespace-nowrap px-6 py-3.5 font-sans text-sm font-semibold text-platinum-200 underline decoration-gold-500 decoration-2 underline-offset-8 transition-colors duration-150 ease-out hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold-400" data-id="element-215" data-mp="2rt5pnrfurecv">
                Contact Yolandi
              </Link>
            </motion.div>
          </div>

          <motion.div custom={2} initial="hidden" animate="visible" variants={fade} className="lg:col-span-5" data-id="element-216" data-mp="25ls6xku6oghp">
            <PortraitFrame data-id="element-217" data-mp="21in5zmpr4mv6" />
          </motion.div>
        </div>
      </section>

      <section className="border-y border-navy-700 bg-navy-950 py-14" aria-label="Guiding principle" data-id="element-218" data-mp="2fd60zd3d8o3g">
        <div className="mx-auto w-full max-w-7xl px-6 lg:px-10" data-id="element-219" data-mp="2aj1s2oaji5zq">
          <figure className="mx-auto max-w-4xl text-center" data-id="element-220" data-mp="1muilqeb1w3aa">
            <blockquote data-id="element-221" data-mp="1hwqt1i2l0d51">
              <p className="font-display text-2xl font-semibold leading-snug tracking-tight text-white sm:text-3xl" data-id="element-222" data-mp="1hx8x30vhjvni">
                “{profile.quote}”
              </p>
            </blockquote>
            <figcaption className="mt-6 font-sans text-sm uppercase tracking-[0.18em] text-gold-400" data-id="element-223" data-mp="3mx1mhrquugzz">
              Financial Services · Energy &amp; Critical Infrastructure · Professional Services
            </figcaption>
          </figure>
        </div>
      </section>

      <SectionDirectory data-id="element-224" data-mp="2yzujm90fty5i" />
    </div>;
}